import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu,
  FormControlLabel,
  Checkbox,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import api from "../../axiosInstance";
import useStatesUniversities from "../../hooks/useStatesUniversities";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import ExamTimeTableModal from "./ExamTimeTableModal";
import UploadExamTimeTableModal from "./UploadExamTimeTableModal";
import BulkUploadExamsModal from "./BulkUploadExamsModal";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

const ACCENT = "#17479e";

const whiteSortLabelSx = {
  color: "white !important",
  "&.Mui-active": { color: "white !important" },
  "&:hover": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const columns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "60px" },
    getValue: (r) => r.id,
  },
  {
    key: "examName",
    label: "Exam Name",
    cellCss: { minWidth: "160px" },
    getValue: (r) => r.examName,
  },
  {
    key: "examType",
    label: "Exam Type",
    cellCss: { minWidth: "130px" },
    getValue: (r) => r.examType,
  },
  {
    key: "examDate",
    label: "Date",
    cellCss: { minWidth: "130px" },
    getValue: (r) => r.examDate,
  },
  {
    key: "time",
    label: "Time",
    cellCss: { minWidth: "130px" },
    getValue: (r) => `${r.examTime ?? ""} – ${r.examEndTime ?? ""}`,
  },
  {
    key: "universityName",
    label: "University",
    cellCss: { minWidth: "180px" },
    getValue: (r) => r.universityName,
  },
  {
    key: "courseName",
    label: "Course",
    cellCss: { minWidth: "180px" },
    getValue: (r) => r.courseName,
  },
  {
    key: "regulationYearName",
    label: "Regulation Year",
    cellCss: { minWidth: "200px" },
    getValue: (r) => r.regulationYearName,
  },
  {
    key: "bookName",
    label: "Book",
    cellCss: { minWidth: "220px" },
    getValue: (r) => r.bookName,
  },
  {
    key: "venue",
    label: "Venue",
    cellCss: { minWidth: "160px" },
    getValue: (r) => r.venue,
  },
  {
    key: "durationMinutes",
    label: "Duration",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.durationMinutes,
  },
  {
    key: "status",
    label: "Status",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.status,
  },
];

const defaultVisibleKeys = new Set([
  "id",
  "examName",
  "examType",
  "examDate",
  "time",
  "universityName",
  "courseName",
  "venue",
  "status",
]);

const STORAGE_KEY = "examTimeTableVisibleColumns";

// Keeps the open dropdown the same width as its closed field (long option
// text truncates with an ellipsis instead of widening the menu), and caps
// its height so long lists scroll instead of growing the page.
const menuPropsForWidth = (width) => ({
  PaperProps: { style: { maxHeight: 300, width } },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
});

const truncatedMenuItemSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const EXAM_TIME_TABLE_FILTER_KEY = "examTimeTableFilters";

function ExamTimeTable() {
  const savedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem(EXAM_TIME_TABLE_FILTER_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [exams, setExams] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // State is a local filter only, used to narrow the University dropdown
  const [stateId, setStateId] = useState(savedFilters.stateId || "");
  const [universityId, setUniversityId] = useState(
    savedFilters.universityId || "",
  );
  const [courseId, setCourseId] = useState(savedFilters.courseId || "");
  const [semesterId, setSemesterId] = useState(savedFilters.semesterId || "");
  const [statusFilter, setStatusFilter] = useState(
    savedFilters.statusFilter || "",
  );
  const [nameFilter, setNameFilter] = useState("");

  const [allCourses, setAllCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);

  const [page, setPage] = useState(savedFilters.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [orderBy, setOrderBy] = useState(savedFilters.orderBy || "examDate");
  const [order, setOrder] = useState(savedFilters.order || "desc");

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(columns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Add / Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);

  // Bulk upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Bulk Upload Exams (academic selection + preview) - separate from the original Upload above
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Persist filters so they survive navigating away and back to this tab
  useEffect(() => {
    localStorage.setItem(
      EXAM_TIME_TABLE_FILTER_KEY,
      JSON.stringify({
        stateId,
        universityId,
        courseId,
        semesterId,
        statusFilter,
        page,
        rowsPerPage,
        orderBy,
        order,
      }),
    );
  }, [
    stateId,
    universityId,
    courseId,
    semesterId,
    statusFilter,
    page,
    rowsPerPage,
    orderBy,
    order,
  ]);

  const { states, universities, loadingStates, loadingUniversities } =
    useStatesUniversities(stateId);

  useEffect(() => {
    api
      .get("/getCourses", { params: { active: true } })
      .then((res) => setAllCourses(res.data?.data ?? []))
      .catch((err) => console.error("Courses fetch error:", err));
    api
      .get("/getSemesters")
      .then((res) => setSemesters(res.data?.data ?? []))
      .catch((err) => console.error("Semesters fetch error:", err));
  }, []);

  const coursesForUniversity = universityId
    ? allCourses.filter((c) => String(c.universityId) === String(universityId))
    : allCourses;

  const selectedCourse = allCourses.find(
    (c) => String(c.id) === String(courseId),
  );

  // Semester options narrow down to whatever the selected course actually offers.
  // course.semesters is an array of raw semester numbers (e.g. [1,2,3]),
  // matching semester.id — not semester.name.
  const semestersForCourse = selectedCourse?.semesters?.length
    ? semesters.filter((s) =>
        selectedCourse.semesters.some((n) => String(n) === String(s.id)),
      )
    : semesters;

  const handleStateChange = (e) => {
    setStateId(e.target.value);
    setUniversityId("");
    setCourseId("");
    setSemesterId("");
    setPage(0);
  };

  const handleUniversityChange = (e) => {
    setUniversityId(e.target.value);
    setCourseId("");
    setSemesterId("");
    setPage(0);
  };

  const load = () => {
    setIsLoading(true);
    const params = { page, size: rowsPerPage, sort: [`${orderBy},${order}`] };
    if (universityId) params.universityId = universityId;
    if (courseId) params.courseId = courseId;
    if (semesterId) params.semesterId = semesterId;
    if (statusFilter) params.status = statusFilter;

    api
      .get("/api/admin/exams", {
        params,
        // Send repeated "sort=field,dir" (Spring Pageable convention) instead
        // of axios' default "sort[]=field,dir"
        paramsSerializer: (p) => {
          const searchParams = new URLSearchParams();
          Object.keys(p).forEach((key) => {
            if (Array.isArray(p[key])) {
              p[key].forEach((value) => searchParams.append(key, value));
            } else {
              searchParams.append(key, p[key]);
            }
          });
          return searchParams.toString();
        },
      })
      .then((res) => {
        setExams(res.data?.data?.content ?? []);
        setTotalElements(res.data?.data?.totalElements ?? 0);
      })
      .catch((err) => {
        console.error("Exams fetch error:", err);
        setError("Failed to load exam time table.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    universityId,
    courseId,
    semesterId,
    statusFilter,
    page,
    rowsPerPage,
    orderBy,
    order,
  ]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
    setPage(0);
  };

  const visibleColumnDefs = columns.filter((c) => visibleColumns.has(c.key));

  const filteredExams = nameFilter.trim()
    ? exams.filter((e) =>
        (e.examName || "").toLowerCase().includes(nameFilter.trim().toLowerCase()),
      )
    : exams;

  const handleAddOpen = () => {
    setEditingExam(null);
    setModalOpen(true);
  };
  const handleEditOpen = (row) => {
    setEditingExam(row);
    setModalOpen(true);
  };
  const handleModalClose = () => setModalOpen(false);

  const handleDeleteOpen = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };
  const handleDeleteClose = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };
  const handleDelete = async () => {
    try {
      await api.delete(`/api/admin/exams/${deleteId}`);
      toast.success("Exam deleted successfully.");
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to delete exam. Please try again.",
      );
    } finally {
      handleDeleteClose();
    }
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by exam name"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{ width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: nameFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setNameFilter("")}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <FormControl size="small" sx={{ width: 160 }}>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={stateId}
                  onChange={handleStateChange}
                  disabled={loadingStates}
                  MenuProps={menuPropsForWidth(160)}
                >
                  <MenuItem value="">All States</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s.id} value={s.id} sx={truncatedMenuItemSx}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>University</InputLabel>
                <Select
                  label="University"
                  value={universityId}
                  onChange={handleUniversityChange}
                  disabled={!stateId || loadingUniversities}
                  MenuProps={menuPropsForWidth(200)}
                >
                  <MenuItem value="">All Universities</MenuItem>
                  {universities.map((u) => (
                    <MenuItem key={u.id} value={u.id} sx={truncatedMenuItemSx}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Course</InputLabel>
                <Select
                  label="Course"
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    setSemesterId("");
                    setPage(0);
                  }}
                  MenuProps={menuPropsForWidth(200)}
                >
                  <MenuItem value="">All Courses</MenuItem>
                  {coursesForUniversity.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={truncatedMenuItemSx}>
                      {c.courseName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 160 }}>
                <InputLabel>Semester</InputLabel>
                <Select
                  label="Semester"
                  value={semesterId}
                  onChange={(e) => {
                    setSemesterId(e.target.value);
                    setPage(0);
                  }}
                  MenuProps={menuPropsForWidth(160)}
                >
                  <MenuItem value="">All Semesters</MenuItem>
                  {semestersForCourse.map((s) => (
                    <MenuItem key={s.id} value={s.id} sx={truncatedMenuItemSx}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  MenuProps={menuPropsForWidth(140)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Typography
                sx={{ marginTop: "10px" }}
                variant="body2"
                color="text.secondary"
              >
                {filteredExams.length} of {totalElements} Exams
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Tooltip title="Refresh" arrow>
                <IconButton size="small" onClick={load}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{
                  sx: {
                    width: 220,
                    maxHeight: 500,
                    overflowY: "auto",
                    borderRadius: 2,
                  },
                }}
              >
                {columns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={
                      <Checkbox
                        checked={visibleColumns.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    }
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddOpen}
                {...BUTTON_STYLES.primary}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Add Exam Time Table
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadModalOpen(true)}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                startIcon={<PlaylistAddIcon />}
                onClick={() => setBulkUploadOpen(true)}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Bulk Upload Exams
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: 2,
            borderRadius: 3,
          }}
        >
          <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {visibleColumnDefs.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.cellCss?.align}
                      sx={{
                        ...c.cellCss,
                        padding: "10px 12px",
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                      sortDirection={orderBy === c.key ? order : false}
                    >
                      {c.key === "time" ? (
                        c.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={whiteSortLabelSx}
                        >
                          {c.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      minWidth: "120px",
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExams.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnDefs.length + 1}
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No exams found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExams.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      {visibleColumnDefs.map((c) => (
                        <TableCell
                          key={c.key}
                          sx={{ padding: "10px 12px", ...c.cellCss }}
                        >
                          {(() => {
                            const value = c.getValue(row);
                            if (c.key === "examName") {
                              return (
                                <Typography
                                  sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                                >
                                  {value}
                                </Typography>
                              );
                            }
                            if (c.key === "examDate") return formatDate(value);
                            if (c.key === "durationMinutes")
                              return value ? `${value} min` : "—";
                            if (c.key === "status") {
                              return (
                                <Chip
                                  label={
                                    value ||
                                    (row.active ? "ACTIVE" : "INACTIVE")
                                  }
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.68rem",
                                    color: row.active ? "#2e7d32" : "#c62828",
                                    backgroundColor: row.active
                                      ? "#e8f5e9"
                                      : "#ffebee",
                                  }}
                                />
                              );
                            }
                            return value || value === 0 ? value : "—";
                          })()}
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px 12px",
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#fff",
                          zIndex: 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="Edit Exam" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditOpen(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Exam" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteOpen(row.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
            }}
          />
        </Paper>
      )}

      <ExamTimeTableModal
        open={modalOpen}
        onClose={handleModalClose}
        exam={editingExam}
        isNew={!editingExam}
        onSuccess={load}
      />

      <UploadExamTimeTableModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={load}
      />

      <BulkUploadExamsModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={load}
      />

      <DeleteConfirmationDialog
        open={confirmOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this exam? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default ExamTimeTable;
